/**
 * EditableSpotCell - Inline spot assignment editor
 *
 * Click to activate dropdown showing available spots filtered by:
 * - Size compatibility
 * - Grouped by zone
 */

import {
  Box,
  Chip,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Spot, Zone } from "../../../Ramp/TemporalTest/types";
import { TableRow as PlacementRow } from "../utils/buildTableRows";
import { ScheduleChange } from "./SpotOccupancyGantt";

interface EditableSpotCellProps {
  row: PlacementRow;
  spots: Spot[];
  zones: Zone[];
  spotIdToName: Record<string, string>;
  enableEditing: boolean;
  onScheduleChanged?: (changes: ScheduleChange[]) => void;
}

export const EditableSpotCell: React.FC<EditableSpotCellProps> = ({
  row,
  spots,
  zones,
  spotIdToName,
  enableEditing,
  onScheduleChanged,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  // Handle click to enter edit mode
  const handleClick = (e: React.MouseEvent) => {
    if (!enableEditing) return;
    e.stopPropagation(); // Prevent row expansion
    setIsEditing(true);
  };

  // Handle spot selection
  const handleChange = (newSpotId: string) => {
    if (!onScheduleChanged) return;

    // Emit schedule change event
    const change: ScheduleChange = {
      aircraftId: row.aircraftId,
      tailNumber: row.tailNumber,
      originalSpotId: row.spotIds[0] || undefined,
      newSpotId: newSpotId,
      originalArrivalBucket: row.arrivalBucket,
      originalDepartureBucket: row.departureBucket,
      newArrivalBucket: row.arrivalBucket,
      newDepartureBucket: row.departureBucket,
      segmentIndex: null, // For now, only support single placements
    };

    onScheduleChanged([change]);
    setIsEditing(false);
  };

  // Filter spots by size compatibility
  const compatibleSpots = spots.filter((spot) =>
    spot.allowedSizes.includes(row.sizeCategory)
  );

  // Group spots by zone
  const spotsByZone = zones.reduce((acc, zone) => {
    const zoneSpots = compatibleSpots.filter((spot) =>
      zone.spotIds.includes(spot.id)
    );
    if (zoneSpots.length > 0) {
      acc[zone.id] = { zone, spots: zoneSpots };
    }
    return acc;
  }, {} as Record<string, { zone: Zone; spots: Spot[] }>);

  // Spots not in any zone
  const unzonedSpots = compatibleSpots.filter(
    (spot) => !zones.some((zone) => zone.spotIds.includes(spot.id))
  );

  if (isEditing) {
    return (
      <Select
        value={row.spotIds[0] || ""}
        onChange={(e) => handleChange(e.target.value)}
        onClose={() => setIsEditing(false)}
        open={true}
        size="small"
        autoFocus
        sx={{
          fontSize: "12px",
          fontFamily: "SF Mono, Consolas, monospace",
          "& .MuiSelect-select": {
            py: 0.5,
            px: 1,
          },
        }}
      >
        {/* Grouped by zone */}
        {Object.values(spotsByZone).map(({ zone, spots: zoneSpots }) => [
          <ListSubheader
            key={`header-${zone.id}`}
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "text.secondary",
              lineHeight: "24px",
            }}
          >
            {zone.name}
          </ListSubheader>,
          ...zoneSpots.map((spot) => (
            <MenuItem
              key={spot.id}
              value={spot.id}
              sx={{
                fontSize: "12px",
                fontFamily: "SF Mono, Consolas, monospace",
                py: 0.75,
              }}
            >
              {spotIdToName[spot.id] || spot.id}
            </MenuItem>
          )),
        ])}

        {/* Unzoned spots */}
        {unzonedSpots.length > 0 && [
          <ListSubheader
            key="header-unzoned"
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "text.secondary",
              lineHeight: "24px",
            }}
          >
            {t('screens.ramp.autoPark.editableSpotCell.otherSpots')}
          </ListSubheader>,
          ...unzonedSpots.map((spot) => (
            <MenuItem
              key={spot.id}
              value={spot.id}
              sx={{
                fontSize: "12px",
                fontFamily: "SF Mono, Consolas, monospace",
                py: 0.75,
              }}
            >
              {spotIdToName[spot.id] || spot.id}
            </MenuItem>
          )),
        ]}
      </Select>
    );
  }

  // Normal display mode
  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        cursor: enableEditing ? "pointer" : "default",
        "&:hover": enableEditing
          ? {
              "& .edit-hint": {
                opacity: 1,
              },
            }
          : {},
      }}
    >
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: "text.primary",
          fontFamily: "SF Mono, Consolas, monospace",
          whiteSpace: 'nowrap',
        }}
      >
        {row.spotSummary}
      </Typography>
      {row.moveCount > 0 && (
        <Chip
          label={`${row.moveCount}×`}
          size="small"
          sx={{
            height: 18,
            fontSize: "10px",
            fontWeight: 600,
            bgcolor: "rgba(237, 108, 2, 0.08)",
            color: "#ed6c02",
            border: "1px solid rgba(237, 108, 2, 0.2)",
            "& .MuiChip-label": { px: 0.75, py: 0 },
          }}
        />
      )}
    </Box>
  );
};
